import React, { useState, useRef, useEffect, useCallback } from 'react';
import { editImage, generateSceneImage, blobToBase64, transcribeDialogueAudio } from '../services/geminiService';
import { getModelForCapability } from '../utils/modelHelper';
import { CloseIcon } from './icons/CloseIcon';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { PlusIcon } from './icons/PlusIcon';
import { MagicWandIcon } from './icons/MagicWandIcon';
import { CropIcon } from './icons/CropIcon';
import { MicrophoneIcon } from './icons/MicrophoneIcon';
import { PencilIcon } from './icons/PencilIcon';
import { UndoIcon } from './icons/UndoIcon';
import { RedoIcon } from './icons/RedoIcon';
import { DownloadIcon } from './icons/DownloadIcon';
import { UserAssetsModal } from './wizard/UserAssetsModal';

// ─── Tool Definitions ───
const TOOLS = [
    { id: 'generate', label: 'Generate', icon: 'sparkles' },
    { id: 'inpaint', label: 'Inpaint', icon: 'paintbrush' },
    { id: 'upscale', label: 'Upscale', icon: 'arrows-expand' },
    { id: 'camera', label: 'Camera Position', icon: 'camera' },
    { id: 'background', label: 'Background', icon: 'photo' },
    { id: 'swap', label: 'Char Swap', icon: 'users' },
    { id: 'crop', label: 'Crop', icon: 'crop' },
];

const CAMERA_ANGLES = [
    'Close-up', 'Wide shot', 'Medium shot', 'Bird\'s eye view',
    'Low angle', 'High angle', 'Dutch angle', 'Over the shoulder',
    'Extreme close-up', 'Worm\'s eye view', 'Point of view (POV)', 'Tracking shot',
];

const PORTRAIT_ANGLES = [
    { id: 'front',       label: 'Front',       angle: 0,   prompt: 'camera is in front of the subject. We see the front of the subject and the background behind them.' },
    { id: 'front-right', label: 'Front-R 45°', angle: 45,  prompt: 'camera has orbited 45 degrees to the front-right. We see the subject\'s front-right and the background that was originally to the subject\'s back-left.' },
    { id: 'right',       label: 'Right',        angle: 90,  prompt: 'camera has orbited 90 degrees to the right side. We see the subject\'s right profile and the background that was originally to the subject\'s left.' },
    { id: 'back-right',  label: 'Back-R 45°',  angle: 135, prompt: 'camera has orbited 135 degrees to the back-right. We see the subject from behind-right and the background that was originally in front-left of the subject.' },
    { id: 'back',        label: 'Back',         angle: 180, prompt: 'camera has orbited 180 degrees to behind the subject. We see the back of the subject and the background that was originally in front of the subject (where the camera used to be).' },
    { id: 'back-left',   label: 'Back-L 45°',  angle: 225, prompt: 'camera has orbited 225 degrees to the back-left. We see the subject from behind-left and the background that was originally in front-right of the subject.' },
    { id: 'left',        label: 'Left',         angle: 270, prompt: 'camera has orbited 270 degrees to the left side. We see the subject\'s left profile and the background that was originally to the subject\'s right.' },
    { id: 'front-left',  label: 'Front-L 45°', angle: 315, prompt: 'camera has orbited 315 degrees to the front-left. We see the subject\'s front-left and the background that was originally to the subject\'s back-right.' },
];

// ─── Portrait Head SVGs for each direction ───
const PortraitHead = ({ angleId, className = '' }) => {
    const heads = {
        front: ( // Full frontal face
            <g><ellipse cx="20" cy="15" rx="9" ry="11" fill="currentColor" opacity="0.9"/>
            <circle cx="16" cy="13" r="1.2" fill="#1a1a2e"/><circle cx="24" cy="13" r="1.2" fill="#1a1a2e"/>
            <ellipse cx="20" cy="17.5" rx="1" ry="0.6" fill="#1a1a2e" opacity="0.5"/>
            <path d="M17 21 Q20 23 23 21" stroke="#1a1a2e" strokeWidth="0.8" fill="none" opacity="0.4"/>
            <rect x="16" y="25" width="8" height="6" rx="2" fill="currentColor" opacity="0.7"/></g>
        ),
        'front-right': ( // 3/4 right
            <g><ellipse cx="21" cy="15" rx="9" ry="11" fill="currentColor" opacity="0.9"/>
            <circle cx="18" cy="13" r="1.1" fill="#1a1a2e"/><circle cx="25" cy="13" r="1" fill="#1a1a2e"/>
            <ellipse cx="22.5" cy="17.5" rx="0.8" ry="0.5" fill="#1a1a2e" opacity="0.5"/>
            <path d="M26 11 Q28 15 26 19" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6"/>
            <rect x="17" y="25" width="8" height="6" rx="2" fill="currentColor" opacity="0.7"/></g>
        ),
        right: ( // Full right profile
            <g><ellipse cx="20" cy="15" rx="8" ry="11" fill="currentColor" opacity="0.9"/>
            <circle cx="22" cy="13" r="1" fill="#1a1a2e"/>
            <path d="M28 15 L30 16 L28 17" fill="currentColor" opacity="0.9"/>
            <ellipse cx="14" cy="14" rx="2" ry="3" fill="currentColor" opacity="0.6"/>
            <rect x="16" y="25" width="8" height="6" rx="2" fill="currentColor" opacity="0.7"/></g>
        ),
        'back-right': ( // Back-right 3/4
            <g><ellipse cx="21" cy="15" rx="9" ry="11" fill="currentColor" opacity="0.9"/>
            <path d="M26 11 Q28 15 26 19" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6"/>
            <ellipse cx="14" cy="14" rx="2" ry="3" fill="currentColor" opacity="0.5"/>
            <rect x="17" y="25" width="8" height="6" rx="2" fill="currentColor" opacity="0.7"/></g>
        ),
        back: ( // Full back
            <g><ellipse cx="20" cy="15" rx="9" ry="11" fill="currentColor" opacity="0.9"/>
            <ellipse cx="13" cy="14" rx="2" ry="3" fill="currentColor" opacity="0.5"/>
            <ellipse cx="27" cy="14" rx="2" ry="3" fill="currentColor" opacity="0.5"/>
            <path d="M15 8 Q20 5 25 8" stroke="#1a1a2e" strokeWidth="0.6" fill="none" opacity="0.3"/>
            <rect x="16" y="25" width="8" height="6" rx="2" fill="currentColor" opacity="0.7"/></g>
        ),
        'back-left': ( // Back-left 3/4
            <g><ellipse cx="19" cy="15" rx="9" ry="11" fill="currentColor" opacity="0.9"/>
            <path d="M14 11 Q12 15 14 19" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6"/>
            <ellipse cx="26" cy="14" rx="2" ry="3" fill="currentColor" opacity="0.5"/>
            <rect x="15" y="25" width="8" height="6" rx="2" fill="currentColor" opacity="0.7"/></g>
        ),
        left: ( // Full left profile
            <g><ellipse cx="20" cy="15" rx="8" ry="11" fill="currentColor" opacity="0.9"/>
            <circle cx="18" cy="13" r="1" fill="#1a1a2e"/>
            <path d="M12 15 L10 16 L12 17" fill="currentColor" opacity="0.9"/>
            <ellipse cx="26" cy="14" rx="2" ry="3" fill="currentColor" opacity="0.6"/>
            <rect x="16" y="25" width="8" height="6" rx="2" fill="currentColor" opacity="0.7"/></g>
        ),
        'front-left': ( // 3/4 left
            <g><ellipse cx="19" cy="15" rx="9" ry="11" fill="currentColor" opacity="0.9"/>
            <circle cx="15" cy="13" r="1" fill="#1a1a2e"/><circle cx="22" cy="13" r="1.1" fill="#1a1a2e"/>
            <ellipse cx="17.5" cy="17.5" rx="0.8" ry="0.5" fill="#1a1a2e" opacity="0.5"/>
            <path d="M14 11 Q12 15 14 19" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6"/>
            <rect x="15" y="25" width="8" height="6" rx="2" fill="currentColor" opacity="0.7"/></g>
        ),
    };
    return (
        <svg viewBox="0 0 40 34" className={className} xmlns="http://www.w3.org/2000/svg">
            {heads[angleId] || heads.front}
        </svg>
    );
};

// ─── SVG Icon Component ───
const ToolIcon = ({ type, className = "w-5 h-5" }) => {
    const icons = {
        sparkles: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />,
        paintbrush: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.764m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />,
        'arrows-expand': <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" /></>,
        camera: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316zM16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />,
        photo: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21zM10.5 8.25a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />,
        users: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />,
        crop: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5" />,
    };
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {icons[type]}
        </svg>
    );
};

// ─── Main Component ───
export const ImageEditorModal = ({ shot, characters, projectData, frameType, initialImage: initialImageOverride, initialPrompt: initialPromptOverride, titleOverride, onClose, onSave, onDeductCredits, onTrackCreditsUsed, onSaveMedia }) => {
    // initialImage / initialPrompt may be provided by the caller (e.g. the new
    // panel-image flow passes the specific panel's image and prompt). When
    // omitted, fall back to the legacy start/end frame fields on the shot.
    const initialImage = (initialImageOverride !== undefined)
        ? initialImageOverride
        : (frameType === 'start' ? shot.startFrameImage : shot.endFrameImage);

    // Core state
    const [history, setHistory] = useState(initialImage ? [initialImage] : []);
    const [historyIndex, setHistoryIndex] = useState(initialImage ? 0 : -1);
    const currentImage = history[historyIndex] ?? null;
    const [activeTool, setActiveTool] = useState('generate');
    const [editorStatus, setEditorStatus] = useState('idle');
    const [progressMsg, setProgressMsg] = useState('');
    const isLoading = editorStatus !== 'idle';
    const [error, setError] = useState(null);

    // Generate tool state
    const [prompt, setPrompt] = useState(
        (initialPromptOverride !== undefined && initialPromptOverride !== null)
            ? initialPromptOverride
            : (frameType === 'start' ? (shot.startFramePrompt || shot.description || '') : (shot.endFramePrompt || shot.description || ''))
    );
    const [useStartFrameAsRef, setUseStartFrameAsRef] = useState(true);
    const [referenceImages, setReferenceImages] = useState([]);
    const [refSource, setRefSource] = useState('upload'); // 'upload' | 'project' | 'assets'
    const [showRefAssets, setShowRefAssets] = useState(false);
    const fileInputRef = useRef(null);

    // Edit/Inpaint state
    const [editPrompt, setEditPrompt] = useState('');
    const [inpaintRefImages, setInpaintRefImages] = useState([]);
    const inpaintFileRef = useRef(null);
    const [inpaintRefSource, setInpaintRefSource] = useState('upload'); // 'upload' | 'project' | 'assets'
    const [showInpaintAssets, setShowInpaintAssets] = useState(false);
    const [maskingEnabled, setMaskingEnabled] = useState(false);
    const [brushSize, setBrushSize] = useState(30);
    const [drawingMode, setDrawingMode] = useState('brush');
    const [isDrawing, setIsDrawing] = useState(false);

    // Camera angle state
    const [selectedAngle, setSelectedAngle] = useState('');
    const [cameraPrompt, setCameraPrompt] = useState('');

    // Background state
    const [bgPrompt, setBgPrompt] = useState('');
    const [bgRefImage, setBgRefImage] = useState(null);
    const [bgSource, setBgSource] = useState('upload'); // 'upload' | 'elements' | 'assets'
    const [showBgAssets, setShowBgAssets] = useState(false);
    const bgFileRef = useRef(null);
    // Character swap state
    const [swapTargetChar, setSwapTargetChar] = useState(null);
    const [swapPrompt, setSwapPrompt] = useState('');
    const [swapUploadedImage, setSwapUploadedImage] = useState(null);
    const [showSwapAssets, setShowSwapAssets] = useState(false);
    const swapFileRef = useRef(null);
    const [swapSource, setSwapSource] = useState('project'); // 'project' | 'upload' | 'assets'

    // Crop state
    const [crop, setCrop] = useState(null);
    const [dragHandle, setDragHandle] = useState(null);
    const [dragStart, setDragStart] = useState(null);

    // Mic state
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);

    const imageRef = useRef(null);
    const drawingCanvasRef = useRef(null);
    const lastPoint = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // ─── History Helpers ───
    const addNewImageState = (newImage) => {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newImage);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    };
    const handleUndo = () => { if (historyIndex > 0) setHistoryIndex(historyIndex - 1); };
    const handleRedo = () => { if (historyIndex < history.length - 1) setHistoryIndex(historyIndex + 1); };

    // ─── Canvas / Masking ───
    const handleCanvasResize = useCallback(() => {
        const image = imageRef.current;
        const canvas = drawingCanvasRef.current;
        if (image && canvas) {
            const { width, height } = image.getBoundingClientRect();
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
        }
    }, []);

    useEffect(() => {
        handleCanvasResize();
        const imageEl = imageRef.current;
        if (!imageEl) return;
        const ro = new ResizeObserver(handleCanvasResize);
        ro.observe(imageEl);
        return () => ro.disconnect();
    }, [currentImage, handleCanvasResize]);

    const getMousePos = (e) => {
        const canvas = drawingCanvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        return { x: (e.clientX - rect.left) * (canvas.width / rect.width), y: (e.clientY - rect.top) * (canvas.height / rect.height) };
    };

    const drawLine = (x0, y0, x1, y1) => {
        const ctx = drawingCanvasRef.current?.getContext('2d');
        if (!ctx) return;
        ctx.lineWidth = brushSize * (drawingCanvasRef.current.width / drawingCanvasRef.current.getBoundingClientRect().width);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        if (drawingMode === 'brush') { ctx.strokeStyle = 'rgba(239, 68, 68, 0.7)'; ctx.globalCompositeOperation = 'source-over'; }
        else { ctx.strokeStyle = 'rgba(0,0,0,1)'; ctx.globalCompositeOperation = 'destination-out'; }
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
    };

    const onCanvasMouseDown = (e) => { if (!maskingEnabled) return; const p = getMousePos(e); if (p) { setIsDrawing(true); lastPoint.current = p; } };
    const onCanvasMouseMove = (e) => { if (!isDrawing || !maskingEnabled) return; const p = getMousePos(e); if (p && lastPoint.current) { drawLine(lastPoint.current.x, lastPoint.current.y, p.x, p.y); lastPoint.current = p; } };
    const onCanvasMouseUp = () => { setIsDrawing(false); lastPoint.current = null; };
    const handleClearMask = () => { const c = drawingCanvasRef.current; if (c) c.getContext('2d')?.clearRect(0, 0, c.width, c.height); };

    const getMaskDataUrl = () => {
        const canvas = drawingCanvasRef.current;
        if (!canvas) return null;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        const src = ctx.getImageData(0, 0, canvas.width, canvas.height);
        let hasMask = false;
        const out = ctx.createImageData(canvas.width, canvas.height);
        for (let i = 0; i < src.data.length; i += 4) {
            if (src.data[i + 3] > 0) { out.data[i] = out.data[i+1] = out.data[i+2] = out.data[i+3] = 255; hasMask = true; }
            else { out.data[i] = out.data[i+1] = out.data[i+2] = 0; out.data[i+3] = 255; }
        }
        if (!hasMask) return null;
        const offscreen = document.createElement('canvas');
        offscreen.width = canvas.width; offscreen.height = canvas.height;
        offscreen.getContext('2d').putImageData(out, 0, 0);
        return offscreen.toDataURL('image/png');
    };

    // ─── Crop ───
    // Parse project aspect ratio for locked cropping
    const getAspectRatio = () => {
        const ar = projectData?.aspectRatio || '16:9';
        const parts = ar.split(':').map(Number);
        if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) return parts[0] / parts[1];
        return 16 / 9;
    };

    const handleStartCrop = () => {
        setMaskingEnabled(false);
        const img = imageRef.current;
        if (!img) return;
        const ratio = getAspectRatio();
        const imgW = img.clientWidth, imgH = img.clientHeight;
        // Fit the largest crop box with the correct aspect ratio inside the image
        let w, h;
        if (imgW / imgH > ratio) {
            h = imgH * 0.8; w = h * ratio;
        } else {
            w = imgW * 0.8; h = w / ratio;
        }
        setCrop({ x: (imgW - w) / 2, y: (imgH - h) / 2, width: w, height: h });
    };
    const handleApplyCrop = () => {
        const img = imageRef.current;
        if (!img || !crop) return;
        const canvas = document.createElement('canvas');
        const sx = img.naturalWidth / img.clientWidth, sy = img.naturalHeight / img.clientHeight;
        canvas.width = crop.width * sx; canvas.height = crop.height * sy;
        canvas.getContext('2d').drawImage(img, crop.x * sx, crop.y * sy, crop.width * sx, crop.height * sy, 0, 0, canvas.width, canvas.height);
        addNewImageState(canvas.toDataURL('image/png'));
        setCrop(null);
    };
    const handleCropMouseDown = (e, handle) => { e.preventDefault(); e.stopPropagation(); if (crop) { setDragHandle(handle); setDragStart({ x: e.clientX, y: e.clientY, crop }); } };

    useEffect(() => {
        const ratio = getAspectRatio();
        const mm = (e) => {
            if (!dragStart || !crop || !dragHandle) return;
            const img = imageRef.current;
            const imgW = img?.clientWidth || 1000;
            const imgH = img?.clientHeight || 600;
            const dx = e.clientX - dragStart.x, dy = e.clientY - dragStart.y;
            let nc = { ...dragStart.crop };

            if (dragHandle === 'move') {
                // Move freely, clamp to image bounds
                nc.x = Math.max(0, Math.min(imgW - nc.width, dragStart.crop.x + dx));
                nc.y = Math.max(0, Math.min(imgH - nc.height, dragStart.crop.y + dy));
            } else {
                // For all resize handles: determine the primary delta, then compute the other dimension from the aspect ratio
                let newW = dragStart.crop.width;
                let newH = dragStart.crop.height;
                let anchorX = dragStart.crop.x; // left edge anchor
                let anchorY = dragStart.crop.y; // top edge anchor

                // Determine which edge(s) move and compute new width from delta
                if (dragHandle.includes('e')) newW = dragStart.crop.width + dx;
                if (dragHandle.includes('w')) newW = dragStart.crop.width - dx;
                if (dragHandle.includes('s') && !dragHandle.includes('e') && !dragHandle.includes('w')) newW = (dragStart.crop.height + dy) * ratio;
                if (dragHandle.includes('n') && !dragHandle.includes('e') && !dragHandle.includes('w')) newW = (dragStart.crop.height - dy) * ratio;

                // Enforce minimum size
                newW = Math.max(40, newW);
                newH = newW / ratio;

                // Clamp to image bounds
                if (dragHandle.includes('w')) {
                    anchorX = dragStart.crop.x + dragStart.crop.width; // right edge is anchor
                    const maxW = anchorX;
                    if (newW > maxW) { newW = maxW; newH = newW / ratio; }
                    nc.x = anchorX - newW;
                } else if (dragHandle.includes('e')) {
                    const maxW = imgW - dragStart.crop.x;
                    if (newW > maxW) { newW = maxW; newH = newW / ratio; }
                    nc.x = dragStart.crop.x;
                } else {
                    // Edge-only (n/s) — keep centered horizontally
                    nc.x = dragStart.crop.x + (dragStart.crop.width - newW) / 2;
                }

                if (dragHandle.includes('n')) {
                    anchorY = dragStart.crop.y + dragStart.crop.height; // bottom edge is anchor
                    const maxH = anchorY;
                    if (newH > maxH) { newH = maxH; newW = newH * ratio; }
                    nc.y = anchorY - newH;
                } else if (dragHandle.includes('s')) {
                    const maxH = imgH - dragStart.crop.y;
                    if (newH > maxH) { newH = maxH; newW = newH * ratio; }
                    nc.y = dragStart.crop.y;
                } else {
                    // Edge-only (e/w) — keep centered vertically
                    nc.y = dragStart.crop.y + (dragStart.crop.height - newH) / 2;
                }

                // Final clamp: ensure crop stays within image
                nc.x = Math.max(0, nc.x);
                nc.y = Math.max(0, nc.y);
                newW = Math.min(newW, imgW - nc.x);
                newH = newW / ratio;
                if (nc.y + newH > imgH) { newH = imgH - nc.y; newW = newH * ratio; }

                nc.width = newW;
                nc.height = newH;
            }
            setCrop(nc);
        };
        const mu = () => setDragStart(null);
        if (dragStart) { window.addEventListener('mousemove', mm); window.addEventListener('mouseup', mu); }
        return () => { window.removeEventListener('mousemove', mm); window.removeEventListener('mouseup', mu); };
    }, [dragStart, crop, dragHandle]);

    // ─── Mic ───
    const handleMicClick = async (setTarget) => {
        if (isRecording) { mediaRecorderRef.current?.stop(); setIsRecording(false); return; }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            mediaRecorderRef.current = mr;
            audioChunksRef.current = [];
            mr.ondataavailable = (e) => audioChunksRef.current.push(e.data);
            mr.onstop = async () => {
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                stream.getTracks().forEach(t => t.stop());
                if (blob.size === 0) return;
                setIsTranscribing(true);
                try {
                    const b64 = await blobToBase64(blob);
                    const text = await transcribeDialogueAudio(b64);
                    setTarget(prev => (prev ? `${prev} ${text}` : text).trim());
                } catch (err) { console.error('Transcription error:', err); }
                finally { setIsTranscribing(false); }
            };
            mr.start();
            setIsRecording(true);
        } catch (err) { console.error('Mic error:', err); }
    };

    // ─── Reference images ───
    const handleFileChange = (e) => {
        Array.from(e.target.files || []).forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => setReferenceImages(prev => [...prev, reader.result]);
            reader.readAsDataURL(file);
        });
    };
    const handleInpaintFileChange = (e) => {
        Array.from(e.target.files || []).forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => setInpaintRefImages(prev => [...prev, reader.result]);
            reader.readAsDataURL(file);
        });
        if (e.target) e.target.value = '';
    };

    // ─── AI Actions ───
    const trackCredits = (result) => {
        if (onTrackCreditsUsed && result?.creditsUsed) onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
    };

    const handleGenerate = async () => {
        setEditorStatus('generating'); setError(null);
        try {
            let modelId = getModelForCapability('professional_image') || getModelForCapability('fast_image');
            let result;
            if (frameType === 'end' && useStartFrameAsRef && shot.startFrameImage) {
                const allRefs = [shot.startFrameImage, ...referenceImages];
                modelId = getModelForCapability('reference_image') || modelId;
                const actionPrompt = `Generate the END FRAME based on: "${prompt}". Reference is the START FRAME. Subject/camera MUST have moved. Keep identity, location, lighting consistent.`;
                result = await generateSceneImage(actionPrompt, projectData.aspectRatio, characters, allRefs, modelId);
            } else {
                const hasRefs = referenceImages.length > 0;
                modelId = hasRefs ? (getModelForCapability('reference_image') || modelId) : modelId;
                result = await generateSceneImage(prompt, projectData.aspectRatio, characters, referenceImages, modelId);
            }
            addNewImageState(result?.image || result);
            trackCredits(result);
        } catch (err) { setError(err.message || 'Failed to generate image.'); }
        finally { setEditorStatus('idle'); }
    };

    const handleInpaint = async () => {
        if (!currentImage || !editPrompt.trim()) return;
        setEditorStatus('editing'); setError(null); setProgressMsg('');
        const maskUrl = getMaskDataUrl();
        try {
            let modelId = getModelForCapability('inpainting') || getModelForCapability('image_editing') || 'default';
            const allRefs = [...inpaintRefImages, ...referenceImages];
            const hasUserRefs = allRefs.length > 0;

            let inpaintPrompt;
            if (maskUrl && hasUserRefs) {
                // Mask + reference: place the referenced content inside the masked region
                inpaintPrompt = `INPAINTING WITH REFERENCE: Place the following INSIDE the white-masked area of the image: ${editPrompt}. The provided reference image(s) show EXACTLY what must appear in the masked region — match the face, appearance, clothing, and identity from the reference precisely. Position and scale the referenced subject to fit naturally within the masked area. CRITICAL: Do NOT modify anything outside the masked region. The rest of the image must stay pixel-perfect unchanged.`;
            } else if (maskUrl) {
                // Mask only, no reference
                inpaintPrompt = `INPAINTING TASK: Edit ONLY the white-masked area of the image. ${editPrompt}. CRITICAL: Do NOT modify any pixels outside the masked region. The rest of the image must remain completely unchanged — same background, same lighting, same characters, same composition.`;
            } else if (hasUserRefs) {
                // Reference only, no mask
                inpaintPrompt = `${editPrompt}. CRITICAL: You MUST use the provided reference image(s) as the EXACT visual guide. Match the appearance, face, clothing, and identity from the reference precisely.`;
            } else {
                inpaintPrompt = editPrompt;
            }

            const fullPrompt = prompt ? `${prompt}. ${inpaintPrompt}` : inpaintPrompt;
            const result = await editImage(currentImage, fullPrompt, characters, allRefs, maskUrl, modelId, projectData.aspectRatio, setProgressMsg);
            addNewImageState(result?.imageUrl || result);
            setEditPrompt(''); handleClearMask();
            trackCredits(result);
        } catch (err) { setError(err.message || 'Failed to edit image.'); }
        finally { setEditorStatus('idle'); setProgressMsg(''); }
    };

    const handleUpscale = async () => {
        if (!currentImage) return;
        setEditorStatus('upscaling'); setError(null); setProgressMsg('');
        try {
            let modelId = getModelForCapability('upscale') || getModelForCapability('image_upscale') || 'default';
            const result = await editImage(currentImage, 'Upscale this image to higher resolution. Enhance details, sharpness, and clarity while maintaining photorealistic quality. If there are people or characters in the image, ensure hyper-realistic skin rendering: visible pores, natural skin texture, subtle blemishes, realistic skin tone variation, and fine facial hair detail. Preserve the exact same composition, colors, lighting, and content. Do not alter, add, or remove any visual elements.', characters, [], null, modelId, projectData.aspectRatio, setProgressMsg);
            addNewImageState(result?.imageUrl || result);
            trackCredits(result);
        } catch (err) { setError(err.message || 'Failed to upscale image.'); }
        finally { setEditorStatus('idle'); setProgressMsg(''); }
    };

    const handleCameraAngle = async () => {
        if (!currentImage || !selectedAngle) return;
        setEditorStatus('camera'); setError(null); setProgressMsg('');
        const shotType = cameraPrompt ? ` Shot type: ${cameraPrompt}.` : '';
        const fullPrompt = `CAMERA REPOSITIONING (NOT character repositioning): Imagine this image is a frozen 3D scene — a movie set where every person, object, and prop is completely locked in place like a statue. Now physically move the CAMERA to a new position: ${selectedAngle}.${shotType}

ABSOLUTE RULES:
1) Every character MUST remain in the EXACT SAME pose, body position, gesture, facial expression, and orientation as the original image. They do NOT turn, shift, lean, or rotate at all — they are frozen statues.
2) The ONLY thing that changes is the camera viewpoint. This means:
   - Different parts of the background become visible (parallax shift)
   - Objects closer to camera appear larger (foreshortening)
   - You see different sides of furniture, walls, and props
   - Depth and spatial relationships between objects change
3) The character's clothing, face, skin tone, accessories, and identity must be IDENTICAL to the reference.
4) Do NOT re-pose, re-light, or re-dress the character. Do NOT change their body angle relative to the scene — only the camera moves.
5) Think of it as: the reference image is frame 1 from camera A. Generate frame 1 from camera B at the new position. Same frozen moment in time.`;
        try {
            let modelId = getModelForCapability('image_ref_to_image') || getModelForCapability('reference_image') || 'default';
            const result = await editImage(currentImage, fullPrompt, characters, [], null, modelId, projectData.aspectRatio, setProgressMsg);
            addNewImageState(result?.imageUrl || result);
            trackCredits(result);
        } catch (err) { setError(err.message || 'Failed to reposition camera.'); }
        finally { setEditorStatus('idle'); setProgressMsg(''); }
    };

    const handleChangeBackground = async () => {
        if (!currentImage || (!bgPrompt.trim() && !bgRefImage)) return;
        setEditorStatus('background'); setError(null); setProgressMsg('');
        const maskUrl = getMaskDataUrl();
        const desc = bgPrompt.trim() || 'Change the background to match the reference image';
        const fullPrompt = `Change the background to: ${desc}. Keep the foreground subject/characters exactly the same. Only replace the background environment.`;
        const refs = bgRefImage ? [bgRefImage] : [];
        try {
            let modelId = getModelForCapability('inpainting') || getModelForCapability('image_editing') || 'default';
            const result = await editImage(currentImage, fullPrompt, characters, refs, maskUrl, modelId, projectData.aspectRatio, setProgressMsg);
            addNewImageState(result?.imageUrl || result);
            setBgPrompt(''); handleClearMask();
            trackCredits(result);
        } catch (err) { setError(err.message || 'Failed to change background.'); }
        finally { setEditorStatus('idle'); setProgressMsg(''); }
    };

    const handleCharacterSwap = async () => {
        const swapImage = swapTargetChar?.image || swapUploadedImage;
        if (!currentImage || !swapImage) return;
        setEditorStatus('swapping'); setError(null); setProgressMsg('');
        const maskUrl = getMaskDataUrl();
        const charName = swapTargetChar?.name || 'the character in the reference image';
        const fullPrompt = `Replace the character in the image with "${charName}". ${swapPrompt ? swapPrompt : ''} The new character must match the reference image exactly. Keep the same pose, scene, background, and lighting.`;
        try {
            let modelId = getModelForCapability('image_ref_to_image') || getModelForCapability('reference_image') || 'default';
            const result = await editImage(currentImage, fullPrompt, characters, [swapImage], maskUrl, modelId, projectData.aspectRatio, setProgressMsg);
            addNewImageState(result?.imageUrl || result);
            setSwapPrompt(''); handleClearMask();
            trackCredits(result);
        } catch (err) { setError(err.message || 'Failed to swap character.'); }
        finally { setEditorStatus('idle'); setProgressMsg(''); }
    };

    const handleSave = () => { if (currentImage) { onSave(currentImage); if (onSaveMedia) onSaveMedia([currentImage]); } };

    const handleDownload = () => {
        if (!currentImage) return;
        const link = document.createElement('a');
        link.href = currentImage;
        // When a titleOverride is supplied (panel-image flow), produce a
        // filename based on it. Otherwise use the legacy frame-based name.
        link.download = titleOverride
            ? `loopai_${shot.id}_${titleOverride.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')}.png`
            : `loopai_frame_${shot.id}_${frameType}.png`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    // When switching tools, enable/disable masking
    useEffect(() => {
        if (activeTool === 'inpaint' || activeTool === 'background' || activeTool === 'swap') {
            setMaskingEnabled(true);
        } else {
            setMaskingEnabled(false);
        }
        if (activeTool === 'crop') handleStartCrop();
        else setCrop(null);
        setError(null);
    }, [activeTool]);

    const title = titleOverride
        ? `Image Editor: ${titleOverride}`
        : `Image Editor: ${frameType.charAt(0).toUpperCase() + frameType.slice(1)} Frame`;

    const availableChars = (characters || []).filter(c => c.image && (c.category === 'character' || !c.category));
    const availableElements = (characters || []).filter(c => c.image && c.category === 'element');
    const allProjectEntities = (characters || []).filter(c => c.image);

    // Reusable tabbed reference image picker
    const renderRefPicker = ({ images, setImages, source, setSource, showAssets, setShowAssets, fileRef, testIdPrefix }) => (
        <div className="space-y-1.5">
            <label className="text-xs text-gray-500">Reference Images</label>
            <div className="flex gap-1 bg-gray-800/60 p-0.5 rounded-lg">
                {[{k:'upload',l:'Upload'},{k:'project',l:'Project'},{k:'assets',l:'Assets'}].map(t => (
                    <button key={t.k} onClick={() => setSource(t.k)}
                        className={`flex-1 py-1 rounded-md text-[11px] font-medium transition-colors ${source === t.k ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                        data-testid={`${testIdPrefix}-source-${t.k}`}>{t.l}</button>
                ))}
            </div>

            {source === 'upload' && (
                <>
                    <input type="file" ref={fileRef} onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        files.forEach(f => { const r = new FileReader(); r.onloadend = () => setImages(prev => [...prev, r.result]); r.readAsDataURL(f); });
                        e.target.value = '';
                    }} accept="image/*" multiple className="hidden" />
                    <button onClick={() => fileRef.current?.click()}
                        className="w-full py-1.5 border border-dashed border-gray-600 rounded-lg hover:border-purple-500 text-xs flex items-center justify-center gap-1.5 text-gray-400 hover:text-gray-200 transition-colors"
                        data-testid={`${testIdPrefix}-upload-btn`}>
                        <PlusIcon className="w-3.5 h-3.5" /> Upload Image
                    </button>
                </>
            )}

            {source === 'project' && (
                allProjectEntities.length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-2">No project images available.</p>
                ) : (
                    <div className="grid grid-cols-4 gap-1.5 max-h-32 overflow-y-auto">
                        {allProjectEntities.map(e => (
                            <button key={e.id} onClick={() => setImages(prev => prev.includes(e.image) ? prev : [...prev, e.image])}
                                className={`rounded overflow-hidden border transition-all ${images.includes(e.image) ? 'border-purple-500 ring-1 ring-purple-500/50' : 'border-gray-700 hover:border-gray-500'}`}
                                data-testid={`${testIdPrefix}-project-${e.id}`}>
                                <img src={e.image} alt={e.name} className="w-full aspect-square object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer"
                                    onError={ev => { ev.target.style.display='none'; }} />
                                <p className="text-[9px] text-center truncate px-0.5 bg-gray-800">{e.name}</p>
                            </button>
                        ))}
                    </div>
                )
            )}

            {source === 'assets' && (
                <button onClick={() => setShowAssets(true)}
                    className="w-full py-2 border border-dashed border-gray-600 rounded-lg hover:border-purple-500 text-xs flex flex-col items-center gap-1 text-gray-400 hover:text-gray-200 transition-colors"
                    data-testid={`${testIdPrefix}-assets-btn`}>
                    <svg className="w-4 h-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                    Browse User Assets
                </button>
            )}

            {images.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {images.map((img, i) => (
                        <div key={i} className="relative w-12 h-12">
                            <img src={img} className="w-full h-full object-cover rounded border border-gray-600" alt={`Ref ${i+1}`} />
                            <button onClick={() => setImages(prev => prev.filter((_,idx) => idx !== i))}
                                className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none text-white">x</button>
                        </div>
                    ))}
                </div>
            )}

            {showAssets && (
                <UserAssetsModal activeTab="all"
                    onSelect={(url) => { setImages(prev => [...prev, url]); setShowAssets(false); }}
                    onClose={() => setShowAssets(false)} />
            )}
        </div>
    );

    // ─── Render crop handles ───
    const renderCropHandles = () => {
        return ['n','s','e','w','ne','nw','se','sw','move'].map(h => (
            <div key={h}
                className={`absolute ${h === 'move' ? 'cursor-move inset-2' : 'cursor-pointer'} ${h !== 'move' ? 'bg-purple-500 border border-white/60 rounded-sm' : ''}`}
                style={{
                    ...(h.includes('n') && { top: -4, height: 8 }), ...(h.includes('s') && { bottom: -4, height: 8 }),
                    ...(h.includes('e') && { right: -4, width: 8 }), ...(h.includes('w') && { left: -4, width: 8 }),
                    ...(!h.includes('n') && !h.includes('s') && { top: 0, bottom: 0 }),
                    ...(!h.includes('e') && !h.includes('w') && { left: 0, right: 0 }),
                }}
                onMouseDown={e => handleCropMouseDown(e, h)}
            />
        ));
    };

    // ─── Render tool panel ───
    const renderToolPanel = () => {
        switch (activeTool) {
            case 'generate':
                return (
                    <div className="space-y-3" data-testid="tool-generate">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Generate Image</h3>
                        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3}
                            className="w-full p-2.5 bg-gray-800/80 border border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                            placeholder="Describe what you want to generate..." data-testid="generate-prompt" />
                        {frameType === 'end' && shot.startFrameImage && (
                            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                                <input type="checkbox" checked={useStartFrameAsRef} onChange={e => setUseStartFrameAsRef(e.target.checked)}
                                    className="rounded border-gray-600 text-purple-600 focus:ring-purple-500" />
                                Use start frame as reference
                            </label>
                        )}
                        {renderRefPicker({
                            images: referenceImages, setImages: setReferenceImages,
                            source: refSource, setSource: setRefSource,
                            showAssets: showRefAssets, setShowAssets: setShowRefAssets,
                            fileRef: fileInputRef, testIdPrefix: 'gen-ref'
                        })}
                        <button onClick={handleGenerate} disabled={isLoading || !prompt.trim()} data-testid="generate-btn"
                            className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2">
                            {editorStatus === 'generating' ? <><LoadingSpinner className="w-4 h-4" /> Generating...</> : <><MagicWandIcon className="w-4 h-4" /> Generate Image</>}
                        </button>
                    </div>
                );

            case 'inpaint':
                return (
                    <div className="space-y-3" data-testid="tool-inpaint">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Inpaint / Edit</h3>
                        <p className="text-xs text-gray-500">Paint over the area you want to edit on the preview, then describe the change.</p>
                        <textarea value={editPrompt} onChange={e => setEditPrompt(e.target.value)} rows={2}
                            className="w-full p-2.5 bg-gray-800/80 border border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                            placeholder="e.g., Add rain, change lighting..." data-testid="inpaint-prompt" />
                        {renderRefPicker({
                            images: inpaintRefImages, setImages: setInpaintRefImages,
                            source: inpaintRefSource, setSource: setInpaintRefSource,
                            showAssets: showInpaintAssets, setShowAssets: setShowInpaintAssets,
                            fileRef: inpaintFileRef, testIdPrefix: 'inpaint-ref'
                        })}
                        <div className="space-y-2">
                            <div className="flex gap-1.5">
                                <button onClick={() => setDrawingMode('brush')} className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${drawingMode === 'brush' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>Brush</button>
                                <button onClick={() => setDrawingMode('eraser')} className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${drawingMode === 'eraser' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>Eraser</button>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 w-8">Size</span>
                                <input type="range" min="10" max="100" value={brushSize} onChange={e => setBrushSize(+e.target.value)} className="flex-1 accent-purple-500" />
                            </div>
                            <button onClick={handleClearMask} className="w-full py-1.5 bg-gray-700 hover:bg-gray-600 rounded-md text-xs transition-colors">Clear Mask</button>
                        </div>
                        <button onClick={handleInpaint} disabled={isLoading || !editPrompt.trim() || !currentImage} data-testid="inpaint-apply-btn"
                            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm">
                            {editorStatus === 'editing' ? 'Applying...' : 'Apply Edit'}
                        </button>
                    </div>
                );

            case 'upscale':
                return (
                    <div className="space-y-3" data-testid="tool-upscale">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Image Upscale</h3>
                        <p className="text-xs text-gray-500">Enhance image resolution and detail quality using AI. The composition stays the same.</p>
                        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 space-y-2">
                            <div className="flex items-center justify-between text-xs"><span className="text-gray-400">Enhancement</span><span className="text-purple-400 font-medium">AI Super Resolution</span></div>
                            <div className="flex items-center justify-between text-xs"><span className="text-gray-400">Details</span><span className="text-gray-300">Sharpness + Clarity boost</span></div>
                        </div>
                        <button onClick={handleUpscale} disabled={isLoading || !currentImage} data-testid="upscale-btn"
                            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2">
                            {editorStatus === 'upscaling' ? <><LoadingSpinner className="w-4 h-4" /> Upscaling...</> : <><ToolIcon type="arrows-expand" className="w-4 h-4" /> Upscale Image</>}
                        </button>
                    </div>
                );

            case 'camera':
                return (
                    <div className="space-y-3" data-testid="tool-camera">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Camera Positioning</h3>

                        {/* Portrait Angle Selector */}
                        <div>
                            <label className="text-xs text-gray-500 mb-1.5 block">Camera Position</label>
                            <div className="grid grid-cols-4 gap-2">
                                {PORTRAIT_ANGLES.map(pa => {
                                    const isSelected = selectedAngle === pa.prompt;
                                    return (
                                        <button key={pa.id} onClick={() => setSelectedAngle(pa.prompt)}
                                            className={`group relative flex flex-col items-center gap-0.5 p-1.5 rounded-lg transition-all duration-200
                                                ${isSelected
                                                    ? 'bg-purple-600/25 border border-purple-500 ring-1 ring-purple-500/40 scale-105'
                                                    : 'bg-gray-800/60 border border-gray-700/60 hover:bg-gray-700/60 hover:border-gray-500 hover:scale-105'}`}
                                            style={{ transformOrigin: 'center center' }}
                                            data-testid={`portrait-angle-${pa.id}`}>
                                            <div className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300
                                                ${isSelected ? 'text-purple-300' : 'text-gray-400 group-hover:text-gray-200'}
                                                group-hover:animate-[headBob_0.6s_ease-in-out]`}>
                                                <PortraitHead angleId={pa.id} className="w-9 h-9" />
                                            </div>
                                            <span className={`text-[9px] font-medium leading-tight text-center transition-colors
                                                ${isSelected ? 'text-purple-300' : 'text-gray-500 group-hover:text-gray-300'}`}>
                                                {pa.label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Cinematic Shot Type (secondary) */}
                        <div>
                            <label className="text-xs text-gray-500 mb-1.5 block">Shot Type (optional)</label>
                            <div className="flex flex-wrap gap-1">
                                {CAMERA_ANGLES.map(angle => (
                                    <button key={angle} onClick={() => setCameraPrompt(prev => prev.includes(angle) ? prev.replace(angle, '').trim() : `${prev} ${angle}`.trim())}
                                        className={`px-2 py-1 rounded text-[10px] font-medium transition-colors
                                            ${cameraPrompt.includes(angle) ? 'bg-sky-600/80 text-white' : 'bg-gray-800 text-gray-500 hover:bg-gray-700 hover:text-gray-300'}`}
                                        data-testid={`shot-type-${angle.replace(/\s+/g, '-').toLowerCase()}`}>
                                        {angle}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <textarea value={cameraPrompt} onChange={e => setCameraPrompt(e.target.value)} rows={2}
                            className="w-full p-2.5 bg-gray-800/80 border border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                            placeholder="Additional instructions (optional)..." data-testid="camera-prompt" />
                        <button onClick={handleCameraAngle} disabled={isLoading || !selectedAngle || !currentImage} data-testid="camera-apply-btn"
                            className="w-full bg-sky-600 hover:bg-sky-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2">
                            {editorStatus === 'camera' ? <><LoadingSpinner className="w-4 h-4" /> Processing...</> : <><ToolIcon type="camera" className="w-4 h-4" /> Apply Position</>}
                        </button>
                    </div>
                );

            case 'background':
                return (
                    <div className="space-y-3" data-testid="tool-background">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Change Background</h3>
                        <p className="text-xs text-gray-500">Select a background reference from your project, upload one, or browse assets.</p>

                        {/* Source tabs */}
                        <div className="flex gap-1 bg-gray-800/60 p-0.5 rounded-lg">
                            {[{k:'upload',l:'Upload'},{k:'elements',l:'Elements'},{k:'assets',l:'Assets'}].map(t => (
                                <button key={t.k} onClick={() => setBgSource(t.k)}
                                    className={`flex-1 py-1.5 rounded-md text-[11px] font-medium transition-colors ${bgSource === t.k ? 'bg-teal-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                                    data-testid={`bg-source-${t.k}`}>{t.l}</button>
                            ))}
                        </div>

                        {/* Upload */}
                        {bgSource === 'upload' && (
                            <div className="space-y-1.5">
                                <input type="file" ref={bgFileRef} accept="image/*" className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const reader = new FileReader();
                                        reader.onloadend = () => setBgRefImage(reader.result);
                                        reader.readAsDataURL(file);
                                        e.target.value = '';
                                    }} />
                                {bgRefImage && bgSource === 'upload' ? (
                                    <div className="relative rounded-lg overflow-hidden border border-teal-500">
                                        <img src={bgRefImage} alt="BG Reference" className="w-full h-24 object-cover" />
                                        <button onClick={() => setBgRefImage(null)}
                                            className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] text-white transition-colors"
                                            data-testid="bg-ref-remove">x</button>
                                    </div>
                                ) : (
                                    <button onClick={() => bgFileRef.current?.click()} data-testid="bg-ref-upload-btn"
                                        className="w-full py-3 border border-dashed border-gray-600 rounded-lg hover:border-teal-500 text-xs flex flex-col items-center gap-1 text-gray-400 hover:text-gray-200 transition-colors">
                                        <ToolIcon type="photo" className="w-5 h-5 opacity-50" />
                                        Upload Reference Image
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Project Elements */}
                        {bgSource === 'elements' && (
                            availableElements.length === 0 ? (
                                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-center">
                                    <p className="text-xs text-gray-500">No elements with images available in this project.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                                    {availableElements.map(el => (
                                        <button key={el.id} onClick={() => setBgRefImage(el.image)}
                                            className={`rounded-lg overflow-hidden border-2 transition-all ${bgRefImage === el.image ? 'border-teal-500 ring-1 ring-teal-500/50' : 'border-gray-700 hover:border-gray-500'}`}
                                            data-testid={`bg-element-${el.id}`}>
                                            <img src={el.image} alt={el.name} className="w-full aspect-square object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer"
                                                onError={e => { e.target.style.display='none'; }} />
                                            <p className="text-[10px] text-center py-0.5 bg-gray-800 truncate px-1">{el.name}</p>
                                        </button>
                                    ))}
                                </div>
                            )
                        )}

                        {/* User Assets */}
                        {bgSource === 'assets' && (
                            <div className="space-y-1.5">
                                {bgRefImage && bgSource === 'assets' ? (
                                    <div className="relative rounded-lg overflow-hidden border border-teal-500">
                                        <img src={bgRefImage} alt="Asset BG" className="w-full h-24 object-cover" />
                                        <button onClick={() => setBgRefImage(null)}
                                            className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] text-white transition-colors"
                                            data-testid="bg-asset-remove">x</button>
                                    </div>
                                ) : (
                                    <button onClick={() => setShowBgAssets(true)} data-testid="bg-assets-btn"
                                        className="w-full py-3 border border-dashed border-gray-600 rounded-lg hover:border-teal-500 text-xs flex flex-col items-center gap-1.5 text-gray-400 hover:text-gray-200 transition-colors">
                                        <svg className="w-5 h-5 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                        </svg>
                                        Browse User Assets
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Selected reference preview (when picked from elements/assets) */}
                        {bgRefImage && bgSource !== 'upload' && (
                            <div className="relative rounded-lg overflow-hidden border border-teal-500/50">
                                <img src={bgRefImage} alt="Selected BG" className="w-full h-20 object-cover opacity-80" />
                                <button onClick={() => setBgRefImage(null)}
                                    className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] text-white"
                                    data-testid="bg-selected-remove">x</button>
                            </div>
                        )}

                        <textarea value={bgPrompt} onChange={e => setBgPrompt(e.target.value)} rows={2}
                            className="w-full p-2.5 bg-gray-800/80 border border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                            placeholder="Describe the background (optional if reference uploaded)..." data-testid="bg-prompt" />
                        <div className="space-y-2">
                            <div className="flex gap-1.5">
                                <button onClick={() => setDrawingMode('brush')} className={`flex-1 py-1.5 rounded-md text-xs font-medium ${drawingMode === 'brush' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Brush</button>
                                <button onClick={() => setDrawingMode('eraser')} className={`flex-1 py-1.5 rounded-md text-xs font-medium ${drawingMode === 'eraser' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Eraser</button>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 w-8">Size</span>
                                <input type="range" min="10" max="100" value={brushSize} onChange={e => setBrushSize(+e.target.value)} className="flex-1 accent-purple-500" />
                            </div>
                            <button onClick={handleClearMask} className="w-full py-1.5 bg-gray-700 hover:bg-gray-600 rounded-md text-xs">Clear Mask</button>
                        </div>
                        <button onClick={handleChangeBackground} disabled={isLoading || (!bgPrompt.trim() && !bgRefImage) || !currentImage} data-testid="bg-apply-btn"
                            className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2">
                            {editorStatus === 'background' ? <><LoadingSpinner className="w-4 h-4" /> Processing...</> : <><ToolIcon type="photo" className="w-4 h-4" /> Change Background</>}
                        </button>

                        {showBgAssets && (
                            <UserAssetsModal
                                activeTab="all"
                                onSelect={(imageUrl) => { setBgRefImage(imageUrl); setShowBgAssets(false); }}
                                onClose={() => setShowBgAssets(false)}
                            />
                        )}
                    </div>
                );

            case 'swap':
                const swapHasImage = swapTargetChar?.image || swapUploadedImage;
                return (
                    <div className="space-y-3" data-testid="tool-swap">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Character Swap</h3>
                        <p className="text-xs text-gray-500">Select or upload a character image, then apply.</p>

                        {/* Source tabs */}
                        <div className="flex gap-1 bg-gray-800/60 p-0.5 rounded-lg">
                            {[{k:'project',l:'Project'},{k:'upload',l:'Upload'},{k:'assets',l:'Assets'}].map(t => (
                                <button key={t.k} onClick={() => setSwapSource(t.k)}
                                    className={`flex-1 py-1.5 rounded-md text-[11px] font-medium transition-colors ${swapSource === t.k ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                                    data-testid={`swap-source-${t.k}`}>{t.l}</button>
                            ))}
                        </div>

                        {/* Project characters */}
                        {swapSource === 'project' && (
                            availableChars.length === 0 ? (
                                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-center">
                                    <p className="text-xs text-gray-500">No characters with portraits available.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-3 gap-2">
                                    {availableChars.map(c => (
                                        <button key={c.id} onClick={() => { setSwapTargetChar(c); setSwapUploadedImage(null); }}
                                            className={`rounded-lg overflow-hidden border-2 transition-all ${swapTargetChar?.id === c.id && !swapUploadedImage ? 'border-purple-500 ring-1 ring-purple-500/50' : 'border-gray-700 hover:border-gray-500'}`}
                                            data-testid={`swap-char-${c.id}`}>
                                            <img src={c.image} alt={c.name} className="w-full aspect-square object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer"
                                                onError={e => { e.target.style.display='none'; }} />
                                            <p className="text-[10px] text-center py-0.5 bg-gray-800 truncate px-1">{c.name}</p>
                                        </button>
                                    ))}
                                </div>
                            )
                        )}

                        {/* Upload */}
                        {swapSource === 'upload' && (
                            <div className="space-y-1.5">
                                <input type="file" ref={swapFileRef} accept="image/*" className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const reader = new FileReader();
                                        reader.onloadend = () => { setSwapUploadedImage(reader.result); setSwapTargetChar(null); };
                                        reader.readAsDataURL(file);
                                        e.target.value = '';
                                    }} />
                                {swapUploadedImage ? (
                                    <div className="relative rounded-lg overflow-hidden border border-purple-500">
                                        <img src={swapUploadedImage} alt="Swap ref" className="w-full h-28 object-cover" />
                                        <button onClick={() => setSwapUploadedImage(null)}
                                            className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] text-white transition-colors"
                                            data-testid="swap-upload-remove">x</button>
                                    </div>
                                ) : (
                                    <button onClick={() => swapFileRef.current?.click()} data-testid="swap-upload-btn"
                                        className="w-full py-4 border border-dashed border-gray-600 rounded-lg hover:border-purple-500 text-xs flex flex-col items-center gap-1.5 text-gray-400 hover:text-gray-200 transition-colors">
                                        <ToolIcon type="users" className="w-6 h-6 opacity-40" />
                                        Upload Character Image
                                    </button>
                                )}
                            </div>
                        )}

                        {/* User Assets */}
                        {swapSource === 'assets' && (
                            <div className="space-y-1.5">
                                {swapUploadedImage && swapSource === 'assets' ? (
                                    <div className="relative rounded-lg overflow-hidden border border-purple-500">
                                        <img src={swapUploadedImage} alt="Asset ref" className="w-full h-28 object-cover" />
                                        <button onClick={() => setSwapUploadedImage(null)}
                                            className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] text-white transition-colors"
                                            data-testid="swap-asset-remove">x</button>
                                    </div>
                                ) : (
                                    <button onClick={() => setShowSwapAssets(true)} data-testid="swap-assets-btn"
                                        className="w-full py-4 border border-dashed border-gray-600 rounded-lg hover:border-purple-500 text-xs flex flex-col items-center gap-1.5 text-gray-400 hover:text-gray-200 transition-colors">
                                        <svg className="w-6 h-6 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                        </svg>
                                        Browse User Assets
                                    </button>
                                )}
                            </div>
                        )}

                        <textarea value={swapPrompt} onChange={e => setSwapPrompt(e.target.value)} rows={2}
                            className="w-full p-2.5 bg-gray-800/80 border border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                            placeholder="Additional swap instructions (optional)..." data-testid="swap-prompt" />
                        <div className="space-y-2">
                            <div className="flex gap-1.5">
                                <button onClick={() => setDrawingMode('brush')} className={`flex-1 py-1.5 rounded-md text-xs font-medium ${drawingMode === 'brush' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Brush</button>
                                <button onClick={() => setDrawingMode('eraser')} className={`flex-1 py-1.5 rounded-md text-xs font-medium ${drawingMode === 'eraser' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Eraser</button>
                            </div>
                            <button onClick={handleClearMask} className="w-full py-1.5 bg-gray-700 hover:bg-gray-600 rounded-md text-xs">Clear Mask</button>
                        </div>
                        <button onClick={handleCharacterSwap} disabled={isLoading || !swapHasImage || !currentImage} data-testid="swap-apply-btn"
                            className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2">
                            {editorStatus === 'swapping' ? <><LoadingSpinner className="w-4 h-4" /> Swapping...</> : <><ToolIcon type="users" className="w-4 h-4" /> Swap Character</>}
                        </button>

                        {showSwapAssets && (
                            <UserAssetsModal
                                activeTab="characters"
                                onSelect={(imageUrl) => { setSwapUploadedImage(imageUrl); setSwapTargetChar(null); setShowSwapAssets(false); }}
                                onClose={() => setShowSwapAssets(false)}
                            />
                        )}
                    </div>
                );

            case 'crop':
                return (
                    <div className="space-y-3" data-testid="tool-crop">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Crop</h3>
                        <p className="text-xs text-gray-500">Drag the handles on the preview to adjust the crop area. Aspect ratio is locked.</p>
                        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between text-xs">
                            <span className="text-gray-400">Locked Ratio</span>
                            <span className="text-purple-400 font-semibold">{projectData?.aspectRatio || '16:9'}</span>
                        </div>
                        {crop && (
                            <div className="flex gap-2">
                                <button onClick={handleApplyCrop} data-testid="crop-apply-btn"
                                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 rounded-lg text-sm">Apply Crop</button>
                                <button onClick={() => { setCrop(null); setActiveTool('generate'); }}
                                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg text-sm">Cancel</button>
                            </div>
                        )}
                    </div>
                );
            default: return null;
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-3" onClick={onClose} data-testid="image-editor-overlay">
            <div className="bg-[#12121a] border border-gray-700/60 rounded-xl w-full max-w-[1200px] max-h-[92vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-testid="image-editor-modal">

                {/* ─── Header ─── */}
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-800 bg-[#12121a]">
                    <h2 className="text-base font-semibold text-gray-100" data-testid="editor-title">{title}</h2>
                    <div className="flex items-center gap-1">
                        <button onClick={handleUndo} disabled={historyIndex <= 0} className="p-1.5 rounded-md hover:bg-gray-700/60 disabled:opacity-25 transition-colors" title="Undo" data-testid="undo-btn"><UndoIcon className="w-4 h-4" /></button>
                        <button onClick={handleRedo} disabled={historyIndex >= history.length - 1} className="p-1.5 rounded-md hover:bg-gray-700/60 disabled:opacity-25 transition-colors" title="Redo" data-testid="redo-btn"><RedoIcon className="w-4 h-4" /></button>
                        <div className="w-px h-5 bg-gray-700 mx-1" />
                        <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-700/60 transition-colors" data-testid="close-btn"><CloseIcon className="w-5 h-5" /></button>
                    </div>
                </div>

                {/* ─── Body ─── */}
                <div className="flex-1 flex overflow-hidden min-h-0">

                    {/* ─── Tool Sidebar ─── */}
                    <div className="w-[72px] bg-[#0e0e16] border-r border-gray-800 flex flex-col items-center py-2 gap-0.5 flex-shrink-0" data-testid="tool-sidebar">
                        {TOOLS.map(tool => {
                            const needsImage = tool.id !== 'generate';
                            const disabled = needsImage && !currentImage;
                            return (
                                <button key={tool.id} onClick={() => !disabled && setActiveTool(tool.id)} disabled={disabled}
                                    className={`w-14 py-2 rounded-lg flex flex-col items-center gap-1 transition-all text-[10px] font-medium
                                        ${activeTool === tool.id ? 'bg-purple-600/20 text-purple-400 border border-purple-500/40' : disabled ? 'text-gray-600 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-800 hover:text-gray-300'}`}
                                    data-testid={`tool-btn-${tool.id}`}>
                                    <ToolIcon type={tool.icon} className="w-5 h-5" />
                                    {tool.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* ─── Preview ─── */}
                    <div className="flex-1 flex items-center justify-center bg-[#0a0a12] p-4 relative min-w-0" data-testid="preview-area">
                        {currentImage ? (
                            <div className="relative max-w-full max-h-full">
                                <img ref={imageRef} src={currentImage} alt="Frame" className="max-w-full max-h-[65vh] object-contain rounded-lg" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                <canvas ref={drawingCanvasRef} className={`absolute top-0 left-0 ${maskingEnabled ? 'cursor-crosshair' : 'pointer-events-none'}`}
                                    onMouseDown={onCanvasMouseDown} onMouseMove={onCanvasMouseMove} onMouseUp={onCanvasMouseUp} onMouseLeave={onCanvasMouseUp} />
                                {crop && (
                                    <div className="absolute border-2 border-purple-500" style={{ left: crop.x, top: crop.y, width: crop.width, height: crop.height }}>
                                        {renderCropHandles()}
                                    </div>
                                )}
                                {maskingEnabled && (
                                    <div className="absolute top-2 left-2 bg-black/70 text-[10px] text-purple-300 px-2 py-1 rounded-md pointer-events-none">
                                        Masking Active
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center text-gray-600">
                                <ToolIcon type="sparkles" className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p className="text-sm">No image yet. Use <span className="text-purple-400">Generate</span> to create one.</p>
                            </div>
                        )}
                        {isLoading && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg" data-testid="loading-overlay">
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                                    <span className="text-sm text-gray-300 capitalize">{editorStatus}...</span>
                                    {progressMsg && <span className="text-xs text-gray-500">{progressMsg}</span>}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ─── Tool Panel ─── */}
                    <div className="w-[280px] bg-[#12121a] border-l border-gray-800 p-4 overflow-y-auto flex-shrink-0" data-testid="tool-panel">
                        {renderToolPanel()}
                        {error && (
                            <div className="mt-3 bg-red-500/10 border border-red-500/40 text-red-400 p-2.5 rounded-lg text-xs" data-testid="error-message">
                                {error}
                            </div>
                        )}
                    </div>
                </div>

                {/* ─── Footer ─── */}
                <div className="flex justify-between items-center px-4 py-3 border-t border-gray-800 bg-[#12121a]">
                    <button onClick={handleDownload} disabled={!currentImage} data-testid="download-btn"
                        className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white text-sm font-medium py-2 px-4 rounded-lg flex items-center gap-1.5 transition-colors">
                        <DownloadIcon className="w-4 h-4" /> Download
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium py-2 px-5 rounded-lg transition-colors" data-testid="cancel-btn">Cancel</button>
                        <button onClick={handleSave} disabled={!currentImage} data-testid="save-btn"
                            className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-semibold py-2 px-5 rounded-lg transition-colors">
                            Save Image
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
